/**
 * Provider Data Transformers
 * Transform between internal app structure and API payload format
 */

import type { Vendor, PropertyVendor } from '../types/providerSearch';
import { APPRAISAL_SOFTWARE_KEY_OPTIONS } from '../constants/providerSearchConstants';
import type {
  ProviderCrudResponse,
  AutoProviderRequest,
  PropertyProviderRequest,
  ProviderCategoryRequest
} from './apiTypes';

// Provider Vendor Type Codes
const PROVIDER_VENDOR_TYPE = {
  AUTO: 11,
  PROPERTY: 60,
} as const;

// Provider Status Mapping (Full text to code and vice versa)
const PROVIDER_STATUS_MAP: Record<string, string> = {
  'Active': 'A',
  'Inactive': 'I',
  'Out of network': 'O',
  'A': 'Active',
  'I': 'Inactive',
  'O': 'Out of network'
};

// Helper to format phone as (XXX)-XXX-XXXX
export const formatPhoneForUI = (value: string) => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * Transform internal Vendor structure to API payload for Add/Update operations
 * @param vendor - Internal vendor object (Vendor | ContractVendor | PropertyVendor)
 * @param category - Vendor category ('auto' | 'contract' | 'property')
 * @returns API-formatted payload
 */
export const transformVendorToApiPayload = (
  vendor: Vendor | PropertyVendor,
  category: 'auto' | 'property'
): ProviderCategoryRequest => {
  if (category === 'auto') {
    const autoVendor = vendor as Vendor;
    // Phone validation: must be exactly 10 digits (numbers only)
    const originalPhone = autoVendor.phone || '';
    const phoneRaw = originalPhone.replace(/\D/g, '');
    // Debug log for phone validation
    if (phoneRaw.length !== 10) {
      throw new Error('Business Phone Number format is invalid');
    }
    const appraisalSoftware: AutoProviderRequest['appraisalSoftware'] = [];

    const resolveAppraisalKey = (id: string | undefined): string => {
      if (!id) return '';
      const found = APPRAISAL_SOFTWARE_KEY_OPTIONS.find(opt => opt.id === id);
      return found?.value || '';
    };

    // Build appraisal software entries from selected dropdown values
    if (autoVendor.appraisalSoftware1 && autoVendor.appraisalSoftware1.trim()) {
      appraisalSoftware.push({
        appraisalSoftwareKey: resolveAppraisalKey(autoVendor.appraisalSoftware1.trim()),
        appraisalSoftwareCd: autoVendor.appraisalSoftware1.trim()
      });
    }
    if (autoVendor.appraisalSoftware2 && autoVendor.appraisalSoftware2.trim()) {
      appraisalSoftware.push({
        appraisalSoftwareKey: resolveAppraisalKey(autoVendor.appraisalSoftware2.trim()),
        appraisalSoftwareCd: autoVendor.appraisalSoftware2.trim()
      });
    }

    const contacts: AutoProviderRequest['contacts'] = [];
    if (vendor.address) {
      contacts.push({
        address: vendor.address.street || '',
        city: vendor.address.city || '',
        state: vendor.address.state || '',
        zip: vendor.address.zipCode || '',
        contactTelephoneNbr: vendor.phone ? vendor.phone.replace(/\D/g, '').slice(0, 10) : '',
      });
    }

    const specialties: AutoProviderRequest['specialties'] = [];
    const specialtyCode = autoVendor.specialityCodeSvc || autoVendor.vendorSpecializations || '';
    if (specialtyCode) {
      specialties.push({ specialtyCode });
    }

    const payload: AutoProviderRequest = {
      providerVendorType: PROVIDER_VENDOR_TYPE.AUTO,
      id: vendor.id,
      providerName: vendor.name || '',
      taxId: String(((autoVendor as any).taxIdTin ?? autoVendor.taxIdentificationNbr ?? '')),
      tiesId: String(autoVendor.tiesId || ''),
      vendorId: vendor.vendorId || '',
      // vendorProfileId: 'anything',
      // businessApprovalStatus: (autoVendor as any).approvalStatus || (autoVendor as any).status || 'active',
      comments: (autoVendor as any).suppressionComment || (autoVendor as any).comments || '',
      // serviceAreaCd: (autoVendor as any).serviceAreaCd || 'statewide',
      providerStatusCd: PROVIDER_STATUS_MAP[(autoVendor as any).suppressionIndicator] || 'active',
      vendorSpecializations: autoVendor.vendorSpecializations || autoVendor.specialityCodeSvc || '',
      appraisalSoftware,
      contacts,
      specialties
    };
    return payload;
  }

  // BELOW SECTION IS FOR PROPERTY VENDORS MAPPING 
  const propertyVendor = vendor as PropertyVendor;

  const contacts: PropertyProviderRequest['contacts'] = [];

  // For 'Statewide' and 'Countywide' service areas, handle special logic
  const stateServiceAreas: string[] = Array.isArray((vendor as PropertyVendor).stateServiceAreas)
    ? (vendor as PropertyVendor).stateServiceAreas as string[]
    : [];
  const countyServiceAreas: string[] = Array.isArray((vendor as PropertyVendor).countyServiceArea)
    ? (vendor as PropertyVendor).countyServiceArea as string[]
    : [];
  const countyServiceState: string = (vendor as PropertyVendor).countyState || '';

  if (
    (category === 'property') &&
    (vendor as PropertyVendor).serviceArea === 'Statewide' &&
    stateServiceAreas.length > 0
  ) {
    // First contact: full data, state = first state
    contacts.push({
      address: vendor.address?.street || '',
      city: vendor.address?.city || '',
      state: stateServiceAreas[0] || '',
      zip: vendor.address?.zipCode || '',
      contactTelephoneNbr: vendor.phone ? vendor.phone.replace(/\D/g, '').slice(0, 10) : '',
      contactId: '',
      county: '',
      country: 'USA',
      contactName: (vendor as PropertyVendor).providerContactName || '',
      contactEmail: (vendor as PropertyVendor).providerContactEmail || '',
      additionalContactName: (vendor as PropertyVendor).additionalContactName || '',
      afterHoursPhoneNumber: (vendor as PropertyVendor).afterHoursPhone || '',
      specialties: []
    });
    // Remaining states: only state field, rest empty
    for (let i = 1; i < stateServiceAreas.length; i++) {
      contacts.push({
        address: '',
        city: '',
        state: stateServiceAreas[i] || '',
        zip: '',
        contactTelephoneNbr: '', // always empty for these
        contactId: '',
        county: '',
        country: '',
        contactName: '',
        contactEmail: '',
        additionalContactName: '',
        afterHoursPhoneNumber: '',
        specialties: []
      });
    }
  } else if (
    (category === 'property') &&
    (vendor as PropertyVendor).serviceArea === 'Countywide' &&
    countyServiceState &&
    countyServiceAreas.length > 0
  ) {
    // First contact: full data, state = countyServiceState, county = first county area
    contacts.push({
      address: vendor.address?.street || '',
      city: vendor.address?.city || '',
      state: countyServiceState,
      zip: vendor.address?.zipCode || '',
      contactTelephoneNbr: vendor.phone ? vendor.phone.replace(/\D/g, '').slice(0, 10) : '',
      contactId: '',
      county: countyServiceAreas[0] || '',
      country: 'USA',
      contactName: (vendor as PropertyVendor).providerContactName || '',
      contactEmail: (vendor as PropertyVendor).providerContactEmail || '',
      additionalContactName: (vendor as PropertyVendor).additionalContactName || '',
      afterHoursPhoneNumber: (vendor as PropertyVendor).afterHoursPhone || '',
      specialties: []
    });
    // Remaining counties: only state and county fields, rest empty
    for (let i = 1; i < countyServiceAreas.length; i++) {
      contacts.push({
        address: '',
        city: '',
        state: countyServiceState,
        zip: '',
        contactTelephoneNbr: '', // always empty for these
        contactId: '',
        county: countyServiceAreas[i] || '',
        country: '',
        contactName: '',
        contactEmail: '',
        additionalContactName: '',
        afterHoursPhoneNumber: '',
        specialties: []
      });
    }
  } else if (vendor.address) {
    contacts.push({
      address: vendor.address.street || '',
      city: vendor.address.city || '',
      state: vendor.address.state || '',
      zip: vendor.address.zipCode || '',
      // Always send only digits to backend
      contactTelephoneNbr: vendor.phone ? vendor.phone.replace(/\D/g, '').slice(0, 10) : '',
      contactId: '',
      county: '',
      country: 'USA',
      contactName: (vendor as PropertyVendor).providerContactName || '',
      contactEmail: (vendor as PropertyVendor).providerContactEmail || '',
      additionalContactName: (vendor as PropertyVendor).additionalContactName || '',
      afterHoursPhoneNumber: (vendor as PropertyVendor).afterHoursPhone || '',
      specialties: []
    });
  }

  const specialties: PropertyProviderRequest['specialties'] = [];
  const propServiceCodes = propertyVendor.serviceCodes || '';
  if (propServiceCodes) {
    propServiceCodes.split(',').map(c => c.trim()).filter(Boolean).forEach(code => {
      specialties.push({ specialtyCode: code });
    });
  }

  const payload: PropertyProviderRequest = {
    providerVendorType: PROVIDER_VENDOR_TYPE.PROPERTY,
    id: vendor.id,
    providerName: vendor.name || '',
    providerId: (propertyVendor as any).providerId || '',
    doingBusinessAs: propertyVendor.dba || '',
    taxId: String((propertyVendor as any).taxIdTin || ''),
    vendorId: vendor.vendorId || '',
    principalOwnerName: propertyVendor.principalOwner || '',
    franchiseId: propertyVendor.franchiseId || '',
    yearsInBusiness: parseInt((propertyVendor as any).yearsInBusiness || '0') || 0,
    comments: (propertyVendor as any).adminComments || '',
    // otherServices: (propertyVendor as any).otherServices || '',
    otherServices: (vendor as PropertyVendor).otherServices || '',
    subcontractorUtilizationPercent: parseInt((vendor as PropertyVendor).subcontractorUsage || '0') || 0,
    open24Hours: (propertyVendor as any).twentyFourHourResponse === 'Yes',
    warrantyTermDuration: String((propertyVendor as any).warrantyPeriod || ''),
    preferredProvider: (propertyVendor as any).preferredProviderStatus === 'Yes',
    // preferredVendorComments: (propertyVendor as any).preferredVendorComments || '',
    credentialingContactName: propertyVendor.credentialProviderContactName || '',
    credentialingContactEmail: propertyVendor.credentialProviderContactEmail || '',
    localLicenseNumber: propertyVendor.localLicense || '',
    localLicenseComments: propertyVendor.localLicenseComment || '',
    stateLicenseNumber: propertyVendor.stateLicense || '',
    stateLicenseComments: propertyVendor.stateLicenseComment || '',
    serviceareacd: (vendor as PropertyVendor).serviceArea || '',
    providerStatusCd: (propertyVendor as any).status || 'active',
    mailingAddressLine1: propertyVendor.mailingAddress?.street || '',
    mailingCity: propertyVendor.mailingAddress?.city || '',
    mailingState: propertyVendor.mailingAddress?.state || '',
    mailingZipCode: propertyVendor.mailingAddress?.zipCode || '',
    contacts,
    specialties,
    businessApprovalStatus: ((vendor as PropertyVendor).customerNetworkCodes ?? '')
      .split(',')
      .map(code => code.trim())
      .filter(Boolean),
    vendorProfileOnboardedDate: (vendor as PropertyVendor).businessStartDate || '',
    claimCenter: (vendor as PropertyVendor).claimCenter || '',
    administrativeNotes: (propertyVendor as any).notes || '',

  };
  return payload;
};

/**
 * Transform API response back to internal Vendor structure
 * @param response - API response payload (can be wrapped or direct provider data)
 * @param category - Vendor category
 * @returns Internal vendor object
 */
export const transformApiResponseToVendor = (
  response: ProviderCrudResponse | any,
  category: 'auto' | 'property'
): Vendor | PropertyVendor => {
  // Handle wrapped response format (from PUT/GET operations)
  const providerData = response.data || response;

  // Base vendor object
  const base: any = {
    id: providerData.id || '',
    name: providerData.providerName,
    vendorId: providerData.vendorId || '',
    address: {
      street: providerData.contacts?.[0]?.address || '',
      city: providerData.contacts?.[0]?.city || '',
      state: providerData.contacts?.[0]?.state || '',
      zipCode: providerData.contacts?.[0]?.zip || ''
    },
    providerContactName: providerData.contacts?.[0]?.contactName || providerData.contactName || '',
    providerContactEmail: providerData.contacts?.[0]?.contactEmail || providerData.contactEmail || '',
    phone: providerData.contacts?.[0]?.contactTelephoneNbr ? formatPhoneForUI(providerData.contacts[0].contactTelephoneNbr) : '',
    services: [],
    status: providerData.providerStatusCd,
    isSelected: false
  };

  // Add category-specific fields
  if (category === 'auto') {
    base.providerId = providerData.providerId || '';
    base.specialityCodeSvc = providerData.specialties[0]?.specialtyCode || '';
    base.taxIdentificationNbr = providerData.taxId ? String(providerData.taxId) : undefined;
    base.tiesId = providerData.tiesId ? String(providerData.tiesId) : undefined;
    base.vendorTypeCode = String(providerData.providerVendorType || '');
    const appraisalList = Array.isArray(providerData.appraisalSoftware) ? providerData.appraisalSoftware : [];
    base.appraisalSoftware1 = appraisalList[0]?.appraisalSoftwareCd || '';
    base.appraisalSoftware2 = appraisalList[1]?.appraisalSoftwareCd || '';
    base.suppressionIndicator = PROVIDER_STATUS_MAP[providerData.providerStatusCd || 'active'] || providerData.providerStatusCd || '';
    // API returns `comments`; map to UI field `suppressionComment`
    base.suppressionComment = providerData.comments || providerData.suppressionComment || '';
    base.vendorSpecializations = providerData.vendorSpecializations || '';
    base.serviceArea = providerData.serviceAreaCd || '';
  } else if (category === 'property') {
    base.dba = providerData.doingBusinessAs || '';
    base.taxIdTin = providerData.taxId || '';
    base.providerId = providerData.providerId || '';
    base.adminComments = providerData.comments || '';
    // base.comments = providerData.comments ? [providerData.comments] : [];
    base.customerNetworkCodes = Array.isArray(providerData.businessApprovalStatus)
      ? providerData.businessApprovalStatus.join(',')
      : (providerData as any).businessApprovalStatus || '';
    // base.businessApprovalStatus = providerData.approvalStatus;
    base.key = false;
    // base.notes = providerData.administrativeNotes ? [providerData.administrativeNotes] : [];
    base.yearsInBusiness = providerData.yearsInBusiness || 0;
    base.businessStartDate = providerData.vendorProfileOnboardedDate || '';
    base.principalOwner = providerData.principalOwnerName || '';
    base.franchiseId = providerData.franchiseId || '';
    base.mailingAddress = {
      street: providerData.mailingAddressLine1,
      city: providerData.mailingCity,
      state: providerData.mailingState,
      zipCode: providerData.mailingZipCode
    };
    // Always use contacts[0] for contactName and contactEmail if available
    base.providerContactName = providerData.contacts?.[0]?.contactName || providerData.contactName || '';
    base.providerContactEmail = providerData.contacts?.[0]?.contactEmail || providerData.contactEmail || '';
    base.credentialProviderContactName = providerData.credentialingContactName || '';
    base.credentialProviderContactEmail = providerData.credentialingContactEmail || '';
    base.localLicense = providerData.localLicenseNumber || '';
    base.localLicenseComment = providerData.localLicenseComments || '';
    base.stateLicense = providerData.stateLicenseNumber || '';
    base.stateLicenseComment = providerData.stateLicenseComments || '';
    base.twentyFourHourResponse = providerData.open24Hours ? 'Yes' : 'No';
    base.preferredProviderStatus = providerData.preferredProvider ? 'Yes' : 'No';
    base.serviceCodes = providerData.specialties.map((s: any) => s.specialtyCode).join(', ');
    base.vendorTypeCode = String(providerData.providerVendorType || '');
    base.claimCenter = providerData.claimCenter || '';
    base.warrantyPeriod = providerData.warrantyTermDuration || '';
    base.afterHoursPhone = providerData.contacts?.[0]?.afterHoursPhoneNumber ? formatPhoneForUI(providerData.contacts[0].afterHoursPhoneNumber) : '';
    base.additionalContactName = providerData.contacts?.[0]?.additionalContactName || '';
    // base.claimCenter = providerData.claimCenter;
    base.subcontractorUsage = String(providerData.subcontractorUtilizationPercent || '');
    base.otherServices = providerData.otherServices || '';
    base.serviceArea = providerData.serviceAreaCd || '';
    // Handle both "Statewide" and "StateWide" variations
    const isStatewide = base.serviceArea.toLowerCase() === 'statewide';
    const isCountywide = base.serviceArea.toLowerCase() === 'countywide';
    const isZipwide = base.serviceArea.toLowerCase() === 'zipwide';

    base.stateServiceAreas = (isStatewide && Array.isArray(providerData.contacts))
      ? providerData.contacts.map((c: any) => c.state).filter((v: string, i: number, arr: string[]) => v && arr.indexOf(v) === i)
      : [];
    base.countyServiceArea = (isCountywide && Array.isArray(providerData.contacts))
      ? providerData.contacts.map((c: any) => c.county).filter((v: string, i: number, arr: string[]) => v && arr.indexOf(v) === i)
      : [];
    base.countyState = (isCountywide && Array.isArray(providerData.contacts))
      ? providerData.contacts[0]?.state || ''
      : '';
    base.zipServiceAreas = (isZipwide && Array.isArray(providerData.contacts))
      ? providerData.contacts.map((c: any) => c.zip).filter((v: string, i: number, arr: string[]) => v && arr.indexOf(v) === i)
      : [];
    base.notes = providerData.notes || '';
    if (providerData.serviceAreaCd !== '') {
      base.address = {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    }
  }

  return base;
};
